// Loading a tool set: the frontend half of the C++ `Ui_load_tool_set` port.
//
// The `load_tool_set` Tauri command parses the saved ini section and returns
// one descriptor per saved dialog; this module maps each descriptor onto the
// Tauri tool windows — graph dialogs become graph/trend/event/XY windows with
// a prepop params string, data grids prepop via the `cfg:tag|...` format, and
// diagnostics windows auto-run the saved query via a localStorage handoff.
// Saved window rectangles position the spawned windows. Dialog types with no
// Tauri equivalent (widget_tool, bode_graph_dialog) are skipped and reported
// back to the caller.

import { DiagnosticsQueryParams } from "../types/dialog-types";
import { openGraphWindow, type GraphMode, type WindowBounds } from "./openGraphWindow";
import { openDataGridWindow, openDiagnosticsWindow } from "./openToolWindows";
import {
  severityMaskToUi,
  severityUiToMask,
  queryTypeFromOrdinal,
  queryTypeToOrdinal,
  filterTypeFromOrdinal,
  filterTypeToOrdinal,
  uiDateTimeToTigerSeconds,
} from "./diagQueryParamsBridge";
import { PLOT_COLOURS } from "./plotColours";
import { TIGER_TO_UNIX_OFFSET_SECS } from "./tigerTime";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

// ── Shapes returned by the `load_tool_set` command (Rust ToolSetDialogJson) ──

export interface ToolSetRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface ToolSetPlot {
  channel_name: string;
  turbine: string;
  /** `#RRGGBB`, present when the C++ plot had an explicit colour. */
  colour?: string | null;
  /** Event-graph model name. */
  model_name?: string | null;
  /** XY plots: Y channel and its turbine. */
  y_channel_name?: string | null;
  y_turbine?: string | null;
  /** Multi-trend-XY per-plot historical range (verbatim ini strings). */
  hist_start_time?: string | null;
  hist_end_time?: string | null;
  /** Multitime plot mode as the C++ `multitime_plot_mode_et` ordinal:
   *  0 CURRENT, 1 HISTORICAL, 2 RELATIVE, 3 REPORT. Tool sets and report
   *  TGraph directives store the number (an Mviews.ini view widget spells the
   *  mode out instead). Absent in sets saved before this was carried. */
  multitime_mode?: number | null;
  /** Event model a RELATIVE/REPORT multitime plot indexes. */
  multitime_model_name?: string | null;
  /** Which event a RELATIVE/REPORT multitime plot picks. */
  relative_index?: number | null;
}

export interface ToolSetDiagQuerySaved {
  query_specified: boolean;
  turbine: string;
  message_id: string;
  message_text: string;
  area: string;
  tag: string;
  severity: number;
  latch_level: number;
  group_turbine: boolean;
  failed_sensor: boolean;
  filter_type: number;
  query_type: number;
  hist: boolean;
  hist_start_time?: number | null;
  hist_end_time?: number | null;
}

export type ToolSetDialog =
  | {
      kind: "graph";
      rect: ToolSetRect | null;
      /** C++ tiger_graph_type_et ordinal. */
      graph_type: number;
      hist: boolean;
      hist_start_time?: number | null;
      hist_end_time?: number | null;
      plots: ToolSetPlot[];
      /** Saved legend column widths (C++ `channel_info_column_N`). Width N
       *  applies to display column N+1 — the Tauri legend's leading checkbox
       *  column is an extra the C++ legend didn't have. */
      channel_info_columns: number[];
    }
  | { kind: "data_grid"; rect: ToolSetRect | null; items: string[] }
  | { kind: "diagnostics"; rect: ToolSetRect | null; query: ToolSetDiagQuerySaved }
  | { kind: "unsupported"; dialog_type: string };

// ── Mapping helpers (exported for tests) ────────────────────────────────────

/** Saved screen rect → spawn bounds; degenerate/missing rects yield undefined
 *  so the window opener falls back to its default size. */
export function boundsFromRect(rect: ToolSetRect | null | undefined): WindowBounds | undefined {
  if (!rect) return undefined;
  const width = rect.right - rect.left;
  const height = rect.bottom - rect.top;
  if (width < 100 || height < 100) return undefined;
  return { x: rect.left, y: rect.top, width, height };
}

/** Saved multitime mode ordinal → the word the backend's `multitime_mode`
 *  param uses. C++ `multitime_plot_mode_et` (tiger_graph_chans.h:70-76):
 *  0 CURRENT, 1 HISTORICAL, 2 RELATIVE, 3 REPORT. Anything else is CURRENT,
 *  matching how the C++ view loader treats an unknown mode. */
export function multiTimeModeTag(mode: number | null | undefined): string {
  switch (mode) {
    case 1:
      return "HISTORICAL";
    case 2:
      return "RELATIVE";
    case 3:
      return "REPORT";
    default:
      return "CURRENT_MULTITIME_PLOT_MODE";
  }
}

/** Event-graph tag for the backend: `MODEL#CHANNEL(SUFFIX)`. The C++ plot
 *  stores the model separately (`use_model_name`/`model_name`), so prepend it
 *  unless the channel name already carries one. */
function eventTag(p: ToolSetPlot): string {
  if (p.model_name && !p.channel_name.includes("#")) {
    return `${p.model_name}#${p.channel_name}`;
  }
  return p.channel_name;
}

function plotColour(p: ToolSetPlot, i: number): string {
  return p.colour || PLOT_COLOURS[i % PLOT_COLOURS.length];
}

/** dd/mm/yyyy — the per-plot date format Trend Multi XY fetches expect. */
const DDMMYYYY_RE = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * Map a saved graph dialog onto a Tauri graph window: the window mode plus the
 * prepop params string its data fetch accepts. Returns null for graph types
 * the port has no window for (distribution, spectrum, waterfall, ...).
 *
 * C++ `tiger_graph_type_et` → window mode / backend `graph_type`:
 *   1 NORMAL, 13 HISTORICAL      → "graph"        (DataGraphMsg, graph_type=2)
 *   3 TREND, 14 HISTORICAL_TREND → "trend"        (TrendGraphMsg, graph_type=1)
 *   9 EVENT                      → "event"        (EventGraphMsg, graph_type=3)
 *   4 XY                         → "xy"           (graph_type=7)
 *   10 TREND_XY, 15 HIST_TREND_XY → "xyTrend"     (graph_type=9)
 *   16 MULTI_TREND_XY            → "xyTrendMulti" (graph_type=6)
 *   7 MULTITIME                  → "multiTime"    (graph_type=5, per-plot window)
 *
 * A saved historical range (`hist=1` + RWTime values, which are Tiger seconds)
 * becomes a `session_type=HIST` fetch with top-level start/end.
 */
export function buildGraphOpen(
  d: Extract<ToolSetDialog, { kind: "graph" }>,
): { mode: GraphMode; params: string } | null {
  if (d.plots.length === 0) return null;

  const histWindow =
    d.hist && d.hist_start_time && d.hist_end_time
      ? { start: d.hist_start_time, end: d.hist_end_time }
      : null;

  // Time-series graphs (graph/trend/event windows share graph.html).
  //
  // `withModel` carries a normal plot's saved model (`use_model_name=1` +
  // `model_name`) through to the backend, which then requests it with
  // MODEL_DATA_NORMAL_GRAPH instead of the non-model GRAPH_DATA_CHANNEL_LIST --
  // the C++ routing in tiger_plot_time.cpp::add_plot_chans_to_list. Only the
  // NORMAL/HISTORICAL branch sets it: C++ tests PLOT_TYPE_TREND_TIME_PLOT
  // BEFORE my_is_model_chan, so a trend plot never takes the model path even
  // with use_model_name=1, and an event plot carries its model in the tag.
  const timeParams = (
    backendType: number,
    isEvent: boolean,
    withModel = false,
  ): string => {
    const parts = [
      `session_type=${histWindow ? "HIST" : "ONLINE"}`,
      `graph_type=${backendType}`,
      `number_of_plots=${d.plots.length}`,
      ...d.plots.flatMap((p, i) => {
        const tag = isEvent ? eventTag(p) : p.channel_name;
        const plot = [
          `plot[${i}].tag_name=${p.turbine}:${tag}`,
          `plot[${i}].colour=${plotColour(p, i)}`,
        ];
        if (isEvent) {
          const hash = tag.indexOf("#");
          if (hash > 0) plot.push(`plot[${i}].model_name=${tag.slice(0, hash)}`);
        } else if (withModel && p.model_name) {
          plot.push(`plot[${i}].model_name=${p.model_name}`);
        }
        return plot;
      }),
    ];
    if (histWindow) {
      parts.push(`start_time=${histWindow.start}`, `end_time=${histWindow.end}`);
    }
    return parts.join("|");
  };

  // Multitime graphs: one window per plot mode, each plot resolving its own
  // (start, end) in the backend. The graph-level window is meaningless here —
  // what the backend needs is the report window: `report_start_time` is the
  // anchor a REPORT-mode plot indexes from (C++ `g_report_start_time`, a
  // global the report engine sets; process_graph puts the report window on
  // the dialog for us) and `report_end_time` the "now" a RELATIVE plot counts
  // back from, so every relative plot in a report is based on the report times.
  const multiTimeParams = (
    reportStart: number | null | undefined,
    reportEnd: number | null | undefined,
  ): string => {
    const parts = [
      "session_type=HIST",
      "graph_type=5",
      `number_of_plots=${d.plots.length}`,
      ...d.plots.flatMap((p, i) => [
        `plot[${i}].tag_name=${p.turbine}:${p.channel_name}`,
        `plot[${i}].colour=${plotColour(p, i)}`,
        `plot[${i}].multitime_mode=${multiTimeModeTag(p.multitime_mode)}`,
        `plot[${i}].multitime_model_event=${p.multitime_model_name ?? ""}`,
        `plot[${i}].multitime_relative_index=${p.relative_index ?? 0}`,
        `plot[${i}].multitime_start_time=${p.hist_start_time ?? ""}`,
        `plot[${i}].multitime_end_time=${p.hist_end_time ?? ""}`,
      ]),
    ];
    // Unlike the other branches this is NOT gated on `hist`: a multitime
    // dialog never sets that flag, the report engine just hangs the window on
    // hist_start_time for this purpose.
    if (reportStart) {
      parts.push(`report_start_time=${reportStart}`);
    }
    if (reportEnd) {
      parts.push(`report_end_time=${reportEnd}`);
    }
    return parts.join("|");
  };

  // XY graphs: each saved plot pairs its base channel (X) with `normxy_y_chan`.
  const xyParams = (backendType: number): string | null => {
    const pairs = d.plots.filter((p) => p.y_channel_name);
    if (pairs.length === 0) return null;
    const parts = [
      `session_type=${histWindow ? "HIST" : "ONLINE"}`,
      `graph_type=${backendType}`,
      `number_of_plots=${pairs.length}`,
      ...pairs.flatMap((p, i) => {
        const plot = [
          `plot[${i}].x_tag_name=${p.turbine}:${p.channel_name}`,
          `plot[${i}].y_tag_name=${p.y_turbine || p.turbine}:${p.y_channel_name}`,
          `plot[${i}].colour=${plotColour(p, i)}`,
        ];
        // Trend Multi XY (graph_type=6) requires a per-plot dd/mm/yyyy range.
        if (
          backendType === 6 &&
          p.hist_start_time && DDMMYYYY_RE.test(p.hist_start_time) &&
          p.hist_end_time && DDMMYYYY_RE.test(p.hist_end_time)
        ) {
          plot.push(
            `plot[${i}].start_date=${p.hist_start_time}`,
            `plot[${i}].end_date=${p.hist_end_time}`,
          );
        }
        return plot;
      }),
    ];
    if (histWindow) {
      parts.push(`start_time=${histWindow.start}`, `end_time=${histWindow.end}`);
    }
    return parts.join("|");
  };

  switch (d.graph_type) {
    case 1: // GRAPH_TYPE_NORMAL
    case 13: // GRAPH_TYPE_HISTORICAL
      return { mode: "graph", params: timeParams(2, false, true) };
    case 3: // GRAPH_TYPE_TREND
    case 14: // GRAPH_TYPE_HISTORICAL_TREND
      return { mode: "trend", params: timeParams(1, false) };
    case 9: { // GRAPH_TYPE_EVENT
      return { mode: "event", params: timeParams(3, true) };
    }
    case 4: { // GRAPH_TYPE_XY
      const params = xyParams(7);
      return params ? { mode: "xy", params } : null;
    }
    case 10: // GRAPH_TYPE_TREND_XY
    case 15: { // GRAPH_TYPE_HISTORICAL_TREND_XY (same backend type 9; the
      // historical window rides in via hist_start_time/hist_end_time)
      const params = xyParams(9);
      return params ? { mode: "xyTrend", params } : null;
    }
    case 16: { // GRAPH_TYPE_MULTI_TREND_XY
      const params = xyParams(6);
      return params ? { mode: "xyTrendMulti", params } : null;
    }
    case 7: // GRAPH_TYPE_MULTITIME
      return { mode: "multiTime", params: multiTimeParams(d.hist_start_time, d.hist_end_time) };
    default:
      return null;
  }
}

/** Tiger seconds → the query dialog's date ("YYYY-MM-DD") + time ("HH:MM")
 *  fields, UTC like the rest of the diagnostics time handling. */
function tigerSecondsToDateTime(secs: number): { date: string; time: string } {
  const iso = new Date((secs - TIGER_TO_UNIX_OFFSET_SECS) * 1000).toISOString();
  return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
}

/**
 * Rebuild the diagnostics query dialog's params from the saved tool-set query
 * so the spawned Diagnostics window can auto-run it. The C++ saves the turbine
 * state as an enum ordinal the port doesn't carry, so it loads as "All States".
 */
export function toolSetDiagQueryToUi(q: ToolSetDiagQuerySaved): DiagnosticsQueryParams {
  const { queryType, groupArea } = queryTypeFromOrdinal(q.query_type);
  const start = q.hist && q.hist_start_time ? tigerSecondsToDateTime(q.hist_start_time) : null;
  const end = q.hist && q.hist_end_time ? tigerSecondsToDateTime(q.hist_end_time) : null;
  return {
    turbine: q.turbine,
    ...severityMaskToUi(q.severity),
    queryType,
    filterType: filterTypeFromOrdinal(q.filter_type),
    messageId: q.message_id,
    messageText: q.message_text,
    messageArea: q.area,
    tagName: q.tag,
    latchCount: String(q.latch_level),
    turbineState: "All States",
    groupTurbine: q.group_turbine,
    groupArea,
    failedSensor: q.failed_sensor,
    isHistorical: q.hist,
    timeRange: {
      startDate: start?.date ?? "",
      startTime: start?.time ?? "",
      endDate: end?.date ?? "",
      endTime: end?.time ?? "",
    },
  };
}

/**
 * Reverse of [`toolSetDiagQueryToUi`]: fold the diagnostics dialog's params
 * back into the saved tool-set query fields. Used when saving a tool set —
 * each open Diagnostics window contributes the params of its last-run query.
 */
export function toolSetDiagQueryFromUi(ui: DiagnosticsQueryParams): ToolSetDiagQuerySaved {
  return {
    query_specified: true,
    turbine: ui.turbine,
    message_id: ui.messageId,
    message_text: ui.messageText,
    area: ui.messageArea,
    tag: ui.tagName,
    severity: severityUiToMask(ui),
    latch_level: parseInt(ui.latchCount || "0", 10) || 0,
    group_turbine: ui.groupTurbine,
    failed_sensor: ui.failedSensor,
    filter_type: filterTypeToOrdinal(ui.filterType),
    query_type: queryTypeToOrdinal(ui.queryType, ui.groupArea),
    hist: ui.isHistorical,
    hist_start_time: ui.isHistorical && ui.timeRange.startDate
      ? uiDateTimeToTigerSeconds(ui.timeRange.startDate, ui.timeRange.startTime, false)
      : null,
    hist_end_time: ui.isHistorical && ui.timeRange.endDate
      ? uiDateTimeToTigerSeconds(ui.timeRange.endDate, ui.timeRange.endTime, true)
      : null,
  };
}

// ── Opening ─────────────────────────────────────────────────────────────────

export interface ToolSetLoadResult {
  /** Windows opened. */
  opened: number;
  /** Saved dialogs that could not be restored (no Tauri equivalent). */
  skipped: string[];
}

/** Open the tool windows for a set of loaded dialog descriptors. */
export function applyToolSetDialogs(dialogs: ToolSetDialog[]): ToolSetLoadResult {
  let opened = 0;
  const skipped: string[] = [];

  for (const d of dialogs) {
    switch (d.kind) {
      case "graph": {
        const open = buildGraphOpen(d);
        if (open) {
          openGraphWindow(
            open.mode,
            open.params,
            boundsFromRect(d.rect),
            d.channel_info_columns.length > 0 ? d.channel_info_columns : undefined,
          );
          opened++;
        } else {
          skipped.push(`graph (type ${d.graph_type})`);
        }
        break;
      }
      case "data_grid":
        openDataGridWindow(d.items, boundsFromRect(d.rect));
        opened++;
        break;
      case "diagnostics": {
        // A saved query auto-runs in the new window (handed off via
        // openDiagnosticsWindow's pendingQuery → localStorage) and the query
        // dialog stays closed; without one the window opens on its query
        // dialog as usual.
        openDiagnosticsWindow(
          d.query.turbine || undefined,
          undefined,
          boundsFromRect(d.rect),
          d.query.query_specified ? toolSetDiagQueryToUi(d.query) : undefined,
        );
        opened++;
        break;
      }
      case "unsupported":
        skipped.push(d.dialog_type);
        break;
    }
  }

  return { opened, skipped };
}

/**
 * Load a tool set by display name and open its windows. Port of the C++ Tool
 * Sets dialog's Load action (`OnToolSetLoadButton` → `Ui_load_tool_set`).
 * Returns null when the set doesn't exist. In the browser/webserver build the
 * descriptors come from `/api/load_tool_set` (scoped to the logged-in user);
 * the window opening then runs the same as under Tauri.
 */
export async function loadToolSet(name: string): Promise<ToolSetLoadResult | null> {
  let dialogs: ToolSetDialog[] | null;
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    dialogs = await invoke<ToolSetDialog[] | null>("load_tool_set", { name });
  } else {
    const resp = await fetch(`/api/load_tool_set?name=${encodeURIComponent(name)}`);
    if (!resp.ok) return null;
    dialogs = (await resp.json()) as ToolSetDialog[] | null;
  }
  if (!dialogs) return null;
  return applyToolSetDialogs(dialogs);
}
